<template>
    <div class="plan-content">
        <div class="plan-cards-wrapper w-sm-100">
            <div class="card-header w-sm-100 sm-mb-10">
                <h5>Plan Details</h5>
                <div class="card-control" :class="{ 'plus': !visibleContent }" @click="toggleVisible">
                    <span class="line" v-if="visibleContent"></span>
                    <i class="plus iatse-icon-plus" v-if="!visibleContent"></i>
                </div>
            </div>

        <!-- General Plan Detail Cards -->
        <transition name="slide-fade">
            <div v-if="visibleContent" class="plan-general-details card card-toggle">
                <div class="plan-general-detail-container card" v-if="dataLoaded">
                    <div class="info-card__icon">
                        <i class="iatse-icon-user-shield"></i>
                    </div>
                    <div class="plan-general-detail">
                        <h5> Current Coverage </h5>
                        <h4 v-if="planSummary.Coverage"> {{ planSummary.Coverage?.Plan ?? 'N/A' }} </h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-calendar-payment-loan"></i>
                    </div>
                    <div class="plan-general-detail">
                        <h5> Current Coverage Period </h5>
                        <h4 v-if="planSummary.Coverage"> {{ planSummary.Coverage.From }} - {{ planSummary.Coverage.To }} </h4>
                        <h4 v-else>N/A</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-donate"></i>
                    </div>
                    <div class="plan-general-detail" v-if="planSummary.Coverage?.MrpBalance > 0">
                        <h5> MRP Balance</h5>
                        <h4>{{ formatCurrency(planSummary.Coverage?.MrpBalance) }}</h4>
                    </div>
                    <div class="plan-general-detail" v-else>
                        <h5> MRP Balance </h5>
                        <h4>-</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card"  v-if="planSummary.RmrpBalance > 0">
                    <div class="info-card__icon">
                        <i class="iatse-icon-donate"></i>
                    </div>
                    <div class="plan-general-detail">
                        <h5> {{ isRmrpFrozen ? 'Frozen R-MRP Balance' : 'R-MRP Balance' }} </h5>
                        <h4>{{ formatCurrency(planSummary.RmrpBalance) }}</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-money-check-edit"></i>
                    </div>
                    <div class="plan-general-detail">
                        <h5> Administrative Fee Met </h5>
                        <h4>{{ planSummary.PlanCDetails?.AdministrativeFee ? 'Met' : 'Not Met' }}</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-legal"></i>
                    </div>
                    <div class="plan-general-detail">
                        <h5> Enrollment Form Sent </h5>
                        <h4>{{ planSummary.PlanCDetails?.EnrollmentForm }}</h4>
                    </div>
                </div>
            </div>
        </transition>

        <!-- General Plan Detail Cards -->
        <transition name="slide-fade">
            <div v-if="visibleContent" class="plan-general-details card card-toggle sm-pdt-10">
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-time-quarter"></i>
                    </div>
                    <div class="plan-general-detail">
                        <div class="plan-detail-info"> <h5> Lapse </h5><i class="iatse-icon-info-tooltip"></i> </div>
                        <h4>{{ planSummary.PlanCDetails?.Lapse }}</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-person-circle-xmark"></i>
                    </div>
                    <div class="plan-general-detail">
                        <div class="plan-detail-info"> <h5> Participation Termination </h5> <i class="iatse-icon-info-tooltip"></i> </div>
                        <h4> {{ planSummary.Coverage?.ParticipantTermination ? 'Yes' : 'No' }} </h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-together-people"></i>
                    </div>
                    <div class="plan-general-detail">
                        <div class="plan-detail-info"> <h5> Combined Account </h5><i class="iatse-icon-info-tooltip"></i> </div>
                        <h4>{{ planSummary.PlanCDetails?.CombinedAccount ? 'Yes' : 'No' }}</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-person-circle-xmark"></i>
                    </div>
                    <div class="plan-general-detail">
                        <div class="plan-detail-info"> <h5> Forfeitures </h5><i class="iatse-icon-info-tooltip"></i></div>
                        <h4>{{ planSummary.PlanCDetails?.Forfeitures }}</h4>
                    </div>
                </div>
                <div class="plan-general-detail-container card">
                    <div class="info-card__icon">
                        <i class="iatse-icon-hr-person"></i>
                    </div>
                    <div class="plan-general-detail mr-10">
                        <div class="plan-detail-info"> <h5> QMSCOs </h5><i class="iatse-icon-info-tooltip"></i></div>
                        <h4>{{ planSummary.PlanCDetails?.HasQmsco ? 'Yes' : 'No' }}</h4>
                    </div>
                </div>
            </div>
        </transition>
    </div>

        <!-- Upcoming Plans Card Navigation -->
        <div class="plan-upcoming">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-md-10">
                        <div class="plan-general-details plan-navigation">
                            <div class="plan-upcoming-navigation">
                                <!-- Previous Period Button -->
                                <a @click.prevent="navigateToPrevious" :disabled="!canMovePrev" class="btn btn-primary btn-navigation btn-prev">
                                    <i class="iatse-icon-arrow-down"></i>
                                </a>

                                <!-- Display only 3 periods -->
                                <div v-for="(period, index) in formattedPeriods" :key="index" :class="['period', { 'current-period': period.IsCurrent, 'active-period': period.IsActive }]">
                                    {{ period.FromFormatted }} - {{ period.ToFormatted }}
                                    <label v-if="period.IsCurrent">Current</label>
                                    <label v-else-if="period.IsUpcoming">{{ period.IsUpcoming }}</label>
                                    <label class="pending-period" v-else-if="period.IsPending !== false">{{ period.IsPending }}</label>
                                    <!-- <label v-else-if="!period.IsCurrent">Upcoming</label> -->
                                </div>

                                <!-- Next Period Button -->
                                <a @click.prevent="navigateToNext" :disabled="!canMoveNext" class="btn btn-primary btn-navigation btn-next">
                                    <i class="iatse-icon-arrow-down"></i>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-2">
                        <a
                        href="#"
                        class="btn btn-tertiary"
                        @click.prevent="openModal('coverage-history', 'Coverage History', '', null, null, null)"
                        >Coverage History</a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Contribution Table -->
        <div class="contribution-table">

            <!-- Contribution Account -->
            <div class="capp-account bordered-card width-30">
                <h4> CAPP Account </h4>
                <div class="contribution-left-table" v-if="dataLoaded && planSummary.CappAccount && Object.keys(planSummary.CappAccount).length > 0">
                    <div class="contribution-left-line">
                        <h5> Previous Balance: </h5>
                        <h5> {{ formatCurrency(planSummary.CappAccount.PREVBAL) }} </h5>
                    </div>
                    <div class="contribution-left-lines">
                        <div class="contribution-left-line">
                            <h5 class="month" :class="{ 'current': selectedMonth === planSummary.CappAccount.CONTRIB1?.MonthName }" @click="selectMonth(planSummary.CappAccount.CONTRIB1?.MonthName)">
                                {{ formatDateToMonth(planSummary.CappAccount.CONTRIB1?.MonthName) }}
                            </h5>
                            <h5 class="flex"> {{ formatCurrency(planSummary.CappAccount.CONTRIB1.Value) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5 class="month" :class="{ 'current': selectedMonth === planSummary.CappAccount.CONTRIB2?.MonthName }" @click="selectMonth(planSummary.CappAccount.CONTRIB2?.MonthName)">
                                {{ formatDateToMonth(planSummary.CappAccount.CONTRIB2?.MonthName) }}
                            </h5>
                            <h5 class="flex"> {{ formatCurrency(planSummary.CappAccount.CONTRIB2.Value) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5 class="month" :class="{ 'current': selectedMonth === planSummary.CappAccount.CONTRIB3?.MonthName }" @click="selectMonth(planSummary.CappAccount.CONTRIB3?.MonthName)">
                                {{ formatDateToMonth(planSummary.CappAccount.CONTRIB3?.MonthName) }}
                            </h5>
                            <h5 class="flex"> {{ formatCurrency(planSummary.CappAccount.CONTRIB3.Value) }} </h5>
                        </div>
                    </div>
                    <div class="contribution-left-line">
                        <h5> Total Contributions </h5>
                        <h5> {{ formatCurrency(planSummary.CappAccount.TOTCONT) }} </h5>
                    </div>
                    <div class="contribution-left-lines">
                        <div class="contribution-left-line">
                            <h5> Admin Fees: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.ADMINFEE) }}  </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5 v-if="planSummary.CappAccount.REIMB !== 0" class="link-text" @click.prevent="openModal('hw-mrp-history', 'Medical Reimbursements History', 'plan-mrp', null, null, null, null, { From: planSummary.MrpFrom , To: planSummary.MrpTo })"> Medical Reimbursements: </h5>
                            <h5 v-else> Medical Reimbursements: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.REIMB) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5> Amount Available: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.AMTAVAIL) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5> Co-Payment: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.COPAY) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5> Premium Payment: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.PREMPAY) }} </h5>
                        </div>
                        <div class="contribution-left-line">
                            <h5> Transfers/Waivers: </h5>
                            <h5> {{ formatCurrency(planSummary.CappAccount.XFERWAIV) }} </h5>
                        </div>
                    </div>
                    <div class="contribution-left-line">
                        <h5> Ending Balance </h5>
                        <h5> {{ formatCurrency(planSummary.CappAccount.BALANCE) }} </h5>
                    </div>
                </div>
            </div>

            <!-- Contribution Tables -->
            <div class="contributions-tables">
                <div class="contributions-table-wrapper">
                    <div class="contributions-table-item">
                        <h4> Contribution Detail </h4>

                        <table>
                            <thead>
                                <tr>
                                    <th>Receipt Date</th>
                                    <th>Wk End</th>
                                    <th>Employer/Production</th>
                                    <th>Report</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody v-if="filteredContributionDetails && filteredContributionDetails.length > 0">
                                <tr v-for="contribution in filteredContributionDetails" :key="contribution.ReportID">
                                    <td>{{ contribution.ReceiptDate }}</td>
                                    <td>{{ contribution.PeriodEnd }}</td>
                                    <td>{{ contribution.Employer }} / {{ contribution.Production }}</td>
                                    <td>{{ contribution.ReportID }}</td>
                                    <td>{{ formatCurrency(contribution.Amount) }}</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="6">No contributions found.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="contributions-table-wrapper">
                    <div class="contributions-table-item">
                        <h4 class="d-flex w-100 align-items-center justify-content-between">
                            Statement Activity
                            <a 
                            href="#"
                            class="btn btn-tertiary"
                            @click.prevent="openModal('hw-capp-payments-history', 'CAPP Statements History', '', null, null, null)"
                            >CAPP Statement/Co-Pay History</a>
                        </h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>Coverage Begins</th>
                                    <th>Type</th>
                                    <th>Date Sent</th>
                                    <th>Status</th>
                                    <th>Due Date</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody v-if="planSummary.CappStatements && planSummary.CappStatements.length > 0">
                                <tr v-for="statement in planSummary.CappStatements" :key="statement.ID">
                                    <td>{{ statement.CoverageBegins }}</td>
                                    <td>{{ statement.Type }}</td>
                                    <td>{{ statement.DateSent }}</td>
                                    <td>{{ statement.Status }}</td>
                                    <td>{{ statement.DueDate }}</td>
                                    <td>{{ formatCurrency(statement.Balance) }}</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="6">No statements found.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12">
                    <p>NOTE: Your CAPP Account represents “Contributions Available for Premium Payments” for the purpose of enrollment in one of the Plan C options (C-1, C-2, C-3, C-4 and MRP). Your contribution amounts have no cash value as it is a notional account. Refer to the <a target="_blank" :href="`${baseUrl}/storage/app/media/documents/IATSE-National-HealthWelfare-Plan-C-SPD.pdf`">Plan C SPD</a> for eligibility, plan options and all other related information.</p>
                </div>
            </div>
        </div>

        <!-- Account Actions -->
        <div class="plan-summary__footer d-flex flex-md-row flex-column w-100">
            <div class="col-12 col-md-4 d-flex flex-column justify-content-between gap-10">
                <h4>Other Coverages</h4>
                <div class="account-actions sm-pdb-10">
                    <!-- Other Coverages -->
                    <OtherCoverages :otherCoverages="planSummary.OtherCoverages"/>
                </div>
            </div>
            <div class="col-12 col-md-8">
                <h4>Account Actions</h4>
                <div class="action-buttons mt-10 flex-md-row flex-column sm-pdl-0 sm-pdr-0">
                    <a href="" class="btn btn-secondary plan-modal"
                        @click.prevent="openModal('add-beneficiary', 'My Beneficiary(ies)', 'plan-tables-striped beneficiary', null, null, null
                        )">
                        Beneficiary(ies)
                    </a>
                    <a
                        href="#"
                        class="btn btn-secondary btn-large plan-modal"
                        @click.prevent="openModal('claims', 'Claims', 'plan-upload plan-card-info plan-c-mrp-claims')">
                        Claims
                    </a>
                    <a href="" @click.prevent="openModal('request-card', 'Request for Insurance ID Card(s)', 'plan-upload plan-card-info plan-profile-update')" class="btn btn-secondary plan-modal">Request ID Card(s)</a>
                    <a href="" class="btn btn-secondary btn-large plan-modal"
                        @click.prevent="openModal('dependents', 'My Dependents', null)">My Dependents
                    </a>
                </div>
            </div>
        </div> <!-- end actions -->
    </div>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useModalStore } from '@/stores/modal';
import { useCoverageStore } from '@/stores/coverage'; 
import { useCurrencyFormatter } from "@/composable/useCurrencyFormatter";
import { PlanCSummary, CoveragePeriodSliderItem } from '@/interfaces/health';
import OtherCoverages from '@/components/participant/health-welfare/OtherCoverages.vue';

export default {
    components: { OtherCoverages },

    setup() {
        const dataLoaded = ref(false);
        const modalStore = useModalStore();
        const coverageStore = useCoverageStore();
        const openModal = modalStore.openModal;
        const { formatCurrency } = useCurrencyFormatter();
        const selectedMonth = ref<string | null>(null);
        const fromDate = modalStore.currentModal?.queryParams?.FromDate as string;
        const toDate = modalStore.currentModal?.queryParams?.ToDate as string;

        const visibleContent = ref(true);

        const toggleVisible = () => {
            visibleContent.value = !visibleContent.value;
        }

        const planClasses = computed(() => {
            return coverageStore.coverage?.Primary?.PlanClass
                ? [coverageStore.coverage.Primary.PlanClass]
                : [];
        });

        // Check if the balance is frozen based on PlanClass
        const isRmrpFrozen = computed(() => {
            return !(planClasses.value.includes(1023) || planClasses.value.includes(1024));
        });

        // Store periods separately to avoid refetching
        const periods = ref<CoveragePeriodSliderItem[]>([]);

        // Plan summary without periods
        const planSummary = ref<PlanCSummary>({
            PersonID: 0,
            Coverage: {
                From: '',
                To: '',
                PlanClass: null,
                Plan: ''
            },
            PlanCDetails: {
                CombinedAccount: false,
                AdministrativeFee: false,
                EnrollmentForm: 'N/A',
                Lapse: 'N/A',
                Forfeitures: 'N/A',
                HasQmsco: false
            },
            CappAccount: {
                PREVBAL: 0,
                CONTRIB1: { MonthName: '', Value: 0 },
                CONTRIB2: { MonthName: '', Value: 0 },
                CONTRIB3: { MonthName: '', Value: 0 },
                ADMINFEE: 0,
                TOTCONT: 0,
                REIMB: 0,
                ADJUST: 0,
                AMTAVAIL: 0,
                COPAY: 0,
                PREMPAY: 0,
                BALANCE: 0,
                XFERWAIV: 0,
                InvoiceID: null,
                InvoicePeriod: null,
                InvoicePlan: null,
            },
            MrpFrom: '',
            MrpTo: '',
            ContributionDetails: [],
            CappStatements: [],
            RmrpBalance: 0,
            MrpBalance: 0,
            OtherCoverages: {
                Retiree: { Show: false },
                C: { Show: false },
                A: { Show: false },
                RMRP: { Show: false }
            }
        });

        const currentPeriodIndex = ref<number>(3); // Starting index for current period

        // Flag to check if periods are already fetched
        const periodsFetched = ref<boolean>(false);

        // Computed to get visible periods (previous, current, next)
        const visiblePeriods = computed(() => {
            const start = Math.max(currentPeriodIndex.value - 1, 0);
            const end = Math.min(currentPeriodIndex.value + 2, periods.value.length);
            return periods.value.slice(start, end);
        });

        const activePeriod = computed(() => periods.value.find(period => period.IsActive));

        const canMovePrev = computed(() => currentPeriodIndex.value > 0);
        const canMoveNext = computed(() => currentPeriodIndex.value < periods.value.length - 1);

        // Helper function to update the IsActive flag
        const updateIsActive = () => {
            periods.value.forEach((period, index) => {
                period.IsActive = index === currentPeriodIndex.value;
            });
        };

        // Fetch data from the API, sending a flag if periods are already fetched
        const fetchData = (fromDate: string | boolean | null = null, toDate: string | boolean | null = null) => {
            if (fromDate === null) {
                fromDate = modalStore.currentModal?.queryParams?.FromDate as string ?? null;
                toDate = modalStore.currentModal?.queryParams?.ToDate as string ?? null;
            }

            const params = {
                FromDate: fromDate,
                ToDate: toDate,
                HasPeriods: periodsFetched.value, // Flag to skip fetching periods
            };

            axios
                .get('/api/participant/health-welfare/plan-c-summary', { params })
                .then((response) => {
                    // If periods have not been fetched yet, store them
                    if (!periodsFetched.value) {
                        periods.value = response.data.Periods;
                        periodsFetched.value = true;

                        // Set IsActive for the initial load
                        updateIsActive();
                    }
                    // Store other plan summary details
                    planSummary.value = {
                        ...response.data,
                        Periods: [] // Remove periods from planSummary as they are stored separately
                    };
                    selectedMonth.value = planSummary.value.CappAccount?.CONTRIB1?.MonthName || null;
                    dataLoaded.value = true;
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                });
        };

        // Navigate to the previous period
        const navigateToPrevious = () => {
            if (canMovePrev.value) {
                dataLoaded.value = false;
                currentPeriodIndex.value--;
                const selectedPeriod = periods.value[currentPeriodIndex.value];
                fetchData(selectedPeriod.From, selectedPeriod.To);

                // Update IsActive status
                updateIsActive();
            }
        };

        // Navigate to the next period
        const navigateToNext = () => {
            if (canMoveNext.value) {
                dataLoaded.value = false;
                currentPeriodIndex.value++;
                const selectedPeriod = periods.value[currentPeriodIndex.value];
                fetchData(selectedPeriod.From, selectedPeriod.To);

                // Update IsActive status
                updateIsActive();
            }
        };

        function selectMonth(monthName: string | null) {
            if (selectedMonth.value === monthName) {
                // Clear the filter if the same month is clicked again
                selectedMonth.value = null;
            } else {
                selectedMonth.value = monthName;
            }
        }

        function getMonthYearFromDate(dateString: string): { month: string, year: string } {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                // Format: MM/DD/YYYY
                const [month, , year] = parts;
                return { month, year };
            } else if (parts.length === 2) {
                // Format: MM/YYYY
                const [month, year] = parts;
                return { month, year };
            } else {
                return { month: '', year: '' };
            }
        }

        function formatFromDate(dateString: string): string {
            const dateParts = dateString.split('/');
            if (dateParts.length !== 3) {
                return dateString; // Return as-is if the format is unexpected
            }
            const [month, , year] = dateParts;
            const dateObj = new Date(parseInt(year), parseInt(month) - 1);
            const options: Intl.DateTimeFormatOptions = { month: 'long' }; // Full month name
            return dateObj.toLocaleDateString('en-US', options);
        }

        function formatToDate(dateString: string): string {
            const dateParts = dateString.split('/');
            if (dateParts.length !== 3) {
                return dateString; // Return as-is if the format is unexpected
            }
            const [month, , year] = dateParts;
            const dateObj = new Date(parseInt(year), parseInt(month) - 1);
            const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' }; // Full month name and year
            return dateObj.toLocaleDateString('en-US', options);
        }

        const formattedPeriods = computed(() => {
            return visiblePeriods.value.map(period => ({
                ...period,
                FromFormatted: formatFromDate(period.From),
                ToFormatted: formatToDate(period.To),
            }));
        });

        const filteredContributionDetails = computed(() => {
            if (!selectedMonth.value) {
                return planSummary.value.ContributionDetails;
            }

            const { month: selectedMonthNum, year: selectedYear } = getMonthYearFromDate(selectedMonth.value);

            return planSummary.value.ContributionDetails.filter(detail => {
                if (!detail.ReceiptDate) return false;

                const { month: detailMonth, year: detailYear } = getMonthYearFromDate(detail.ReceiptDate);

                return detailMonth === selectedMonthNum && detailYear === selectedYear;
            });
        });

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const formatDateToMonth = (dateString: string) => {
            const [month, year] = dateString.split('/');
            const monthIndex = parseInt(month, 10) - 1;
            return `${monthNames[monthIndex]} ${year}`;
        };

        onMounted(() => {
            fetchData(fromDate, toDate); // Initial fetch on mount
        });

        return {
            openModal,
            fromDate,
            toDate,
            planSummary,
            isRmrpFrozen,
            // periods, Don't think we need this since we return visiblePeriods
            currentPeriodIndex,
            visiblePeriods,
            activePeriod,
            canMovePrev,
            canMoveNext,
            dataLoaded,
            navigateToPrevious,
            navigateToNext,
            formatDateToMonth,
            formatCurrency,
            filteredContributionDetails,
            getMonthYearFromDate,
            selectedMonth,
            selectMonth,
            //
            formattedPeriods,
            formatFromDate,
            formatToDate,
            //
            baseUrl: process.env.VUE_APP_PUBLIC_URL,
            //
            visibleContent,
            toggleVisible
        };
    },
};
</script>